import React, { useEffect, useState } from 'react';
import { PRICING, formatPrice, getBoost4HourPerHourPrice } from '../config/pricing';
import { Zap, Loader } from 'lucide-react';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

const VideoBoostPanel = ({ token }) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [purchasing, setPurchasing] = useState(null);
  const [error, setError] = useState(null);

  const fetchStatus = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await fetch(`${API}/lounge/boost-status`, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : '',
        },
      });
      if (!res.ok) throw new Error('Failed to load boost status');
      const data = await res.json();
      setStatus(data);
    } catch (err) {
      setError(err.message || 'Could not load boost status');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
  }, []);

  const purchase = async (boostType) => {
    try {
      setPurchasing(boostType);
      setError(null);
      
      // Call backend to create Stripe Checkout Session
      const res = await fetch(`${API}/lounge/boost`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : '',
        },
        body: JSON.stringify({ product: boostType }),
      });
      
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.detail || 'Failed to create checkout session');
      }
      
      const data = await res.json();
      
      // Redirect to Stripe Checkout
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (err) {
      setError(err.message || 'Could not complete purchase');
      alert('Purchase failed. Please try again.');
      setPurchasing(null);
    }
  };

  return (
    <div className="mt-4 rounded-2xl bg-gradient-to-br from-slate-900/90 to-slate-800/90 p-4 text-slate-100 border border-slate-700 shadow-lg">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-200 flex items-center gap-2">
            <Zap className="w-4 h-4 text-amber-400" />
            Need More Time?
          </h3>
          <p className="text-xs text-slate-400 mt-1">
            Buy a one-time boost and extend today's video limit.
          </p>
        </div>
        {status && status.video_boost_seconds > 0 && (
          <div className="text-right text-xs">
            <div className="font-semibold text-emerald-300">
              +{Math.floor(status.video_boost_seconds / 60)} min
            </div>
            <div className="text-slate-400 text-[11px]">
              extra time
            </div>
          </div>
        )}
      </div>

      {error && (
        <div className="mb-2 rounded-md bg-red-900/40 px-3 py-2 text-xs text-red-200 border border-red-800/40">
          ⚠️ {error}
        </div>
      )}

      {loading && (
        <div className="text-xs text-slate-400 flex items-center gap-2 py-2">
          <Loader className="w-4 h-4 animate-spin" />
          Loading…
        </div>
      )}

      <div className="mt-3 grid grid-cols-2 gap-3">
        {/* 1 Hour Boost */}
        <button
          type="button"
          disabled={!!purchasing}
          onClick={() => purchase('boost_1h')}
          className="rounded-xl border border-slate-600 bg-slate-800/60 px-3 py-3 text-left hover:bg-slate-700/60 hover:border-slate-500 disabled:opacity-60 disabled:cursor-not-allowed transition-all"
        >
          <div className="font-semibold text-slate-50 text-sm flex items-center gap-1">
            <Zap className="w-3 h-3 text-blue-400" />
            1 Hour Boost
          </div>
          <div className="text-[11px] text-slate-400 mt-1">
            Perfect for a quick session.
          </div>
          <div className="mt-2 font-bold text-blue-300 text-base">
            {formatPrice(PRICING.boost1Hour)}
          </div>
        </button>

        {/* 4 Hour Bundle - Best Value */}
        <button
          type="button"
          disabled={!!purchasing}
          onClick={() => purchase('boost_4h')}
          className="relative rounded-xl border-2 border-amber-500/70 bg-gradient-to-br from-amber-900/40 to-orange-900/40 px-3 py-3 text-left hover:from-amber-900/60 hover:to-orange-900/60 hover:border-amber-400 disabled:opacity-60 disabled:cursor-not-allowed transition-all"
        >
          <div className="absolute -top-2 -right-2 bg-amber-500 text-amber-950 text-[10px] font-bold px-2 py-0.5 rounded-full shadow-lg">
            BEST VALUE
          </div>
          <div className="font-semibold text-amber-50 text-sm flex items-center gap-1">
            <Zap className="w-3 h-3 text-amber-300" />
            4 Hour Bundle
          </div>
          <div className="text-[11px] text-amber-100/80 mt-1">
            Best for long sessions.
          </div>
          <div className="mt-2 font-bold text-amber-300 text-base flex items-baseline gap-1">
            {formatPrice(PRICING.boost4Hours)}
            <span className="text-[10px] font-normal text-amber-200/70">
              (~${getBoost4HourPerHourPrice()}/hr)
            </span>
          </div>
        </button>
      </div>

      {purchasing && (
        <div className="mt-3 text-xs text-slate-300 flex items-center gap-2 bg-slate-800/60 rounded-lg px-3 py-2">
          <Loader className="w-3 h-3 animate-spin" />
          Completing purchase…
        </div>
      )}

      {status && status.video_boost_seconds > 0 && (
        <div className="mt-3 pt-3 border-t border-slate-700/50">
          <div className="text-[11px] text-slate-400">
            <span className="text-slate-300 font-medium">Active Boost:</span>{' '}
            {Math.floor(status.video_boost_seconds / 3600)} hours{' '}
            {Math.floor((status.video_boost_seconds % 3600) / 60)} minutes
            {status.video_boost_last_updated && (
              <span className="text-slate-500 ml-2">
                • Last updated: {new Date(status.video_boost_last_updated).toLocaleDateString()}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default VideoBoostPanel;
